import React, { FormEvent, useCallback, useEffect, useReducer, useState } from "react";
import { ItemCategoryModel } from "../models/ItemCategoryModel";
import itemService from "../services/itemService";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ItemImage, ItemImageFile, ItemModel } from "../models/ItemModel";
import ItemImageUploadComp from "../components/ItemImageUploadComp";
import { useCategorySelector } from "../hooks/useItemCategorySelector";
import { useImageUpload } from "../hooks/useImageUpload";
import { usePrimaryImageHandler } from "../hooks/usePrimaryImageHandler";
import itemFormReducer, { ItemFormState, ItemValidatonErrors } from "../reducers/itemFormReducer";
import { set } from "date-fns";



const EditItemPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { item: passedItem, categories } = location.state || {};
    const { itemId } = useParams<{ itemId: string }>();

    const initialState: ItemFormState = {
        item: new ItemModel(passedItem ?? {}),
        errors: {},
        isSubmitting: false,
        isDeleting: false,
        serviceSuccess: "",
        serviceError: "",
    };
    const [state, dispatch] = useReducer(itemFormReducer, initialState);




    // Category dropdown state and functions
    const { selectedRootCategory, setSelectedRootCategory, selectedSubCategory, setSelectedSubCategory, subCategories,getSubCategories, initCategoryDropdown } = useCategorySelector(categories);

    // Image upload state and functions
    const { images, setImages, imagesToDelete, setImagesToDelete, uploadItemImages, deleteItemImages, uploadProgress, setUploadProgress } = useImageUpload();
    // Primary image handler
    const { updatePrimaryImage } = usePrimaryImageHandler();

    const loadItem = useCallback(async (itemId: string) => {
        if (!itemId) return;
        if (!state.item?.itemId) {
            const fetchedItem = await itemService.getItem(parseInt(itemId));
            dispatch({ type: "SET_ITEM", payload: fetchedItem });
            setImages(fetchedItem.images);
        }
        else {
            setImages(state.item.images); // Assuming images are stored as file names
        }
    }, [setImages]);

    useEffect(() => {
        if (itemId) {
            loadItem(itemId);
        }
    }, [itemId, loadItem]);

    useEffect(() => {
        if (state.item?.categoryId) {
            initCategoryDropdown(state.item.categoryId);
        }
    }, [state.item?.categoryId, initCategoryDropdown]);


    const validateItemForm = () => {
        const errors: ItemValidatonErrors = {};
        const { itemName, description, price } = state.item;

        if (!itemName) {
            errors.itemName = "Item Name is required";
        }
        else if (itemName.length < 3) {
            errors.itemName = "Item Name must be at least 3 characters long";
        }

        if (!description) {
            errors.description = "Description is required";
        }

        if (price === null || price === undefined || isNaN(price)) {
            errors.price = "Price is required";
        }
        else if (price < 0) {
            errors.price = "Price must be greater than or equal to 0";
        }

        if (!selectedRootCategory) {
            errors.rootCategory = "Category is required";
        }

        if (subCategories && subCategories.length > 0 && (!selectedSubCategory || !state.item.categoryId)) {
            errors.subCategory = "Subcategory is required";
        }

        if (images.length === 0) {
            errors.primaryImage = "At least one photo is required";
        }

        // if (featured && images.length < 3) {
        //     errors.featured = "Featured items must have at least 3 photos";
        // }

        dispatch({ type: "SET_ERRORS", payload: errors });
        return Object.keys(errors).length === 0;
    }





    const handleEditItemSubmit = async (e: FormEvent) => {
        e.preventDefault();
        dispatch({ type: "SET_ERROR", payload: "" });
        dispatch({ type: "SET_SUCCESS", payload: "" });

        if (validateItemForm()) {
            dispatch({ type: "SET_SUBMITTING", payload: true });

            try {

                // Update the item with the new data except images
                const updateResult = await itemService.updateItem(state.item);
                if (updateResult !== "Success") {
                    throw new Error("Failed to update item. Please try again.");
                }

                //delete images that are marked for deletion
                if (imagesToDelete && imagesToDelete.length > 0) {
                    // Delete images that are marked for deletion
                    await deleteItemImages(imagesToDelete);

                    // Remove them from the reducer state
                    dispatch({
                        type: "SET_ITEM",
                        payload: {
                            images: state.item.images.filter(
                                img => !imagesToDelete.map(x => x.imageName).includes(img.fileName)
                            )
                        }
                    });

                    // Clear the images to delete after deletion
                    setImagesToDelete([]);
                }

                // Upload images if any
                const filesToUpload = images
                    .filter((img): img is ItemImageFile => img instanceof ItemImageFile)
                    .map(img => img.file);
                if (filesToUpload && filesToUpload.length > 0) {
                    // Upload images and get the results
                    const uploadResults = await uploadItemImages(state.item.itemId, filesToUpload);
                    if (!uploadResults || uploadResults.length === 0) {
                        throw new Error("Failed to upload images. Please try again.");
                    }

                    setUploadProgress(0); // Reset upload progress after upload

                    // Update the images state with the uploaded results
                    // This assumes that uploadResults contains ItemImage 
                    setImages(prevImages =>
                        prevImages.map(img => {
                            if (img instanceof ItemImageFile) {
                                const match = uploadResults.find(res => res.clientFileName === img.file.name);
                                const uploadedImg = new ItemImage(match);
                                uploadedImg.sortOrder = img.sortOrder; // Preserve sort order
                                return uploadedImg;
                            }
                            return img;
                        })
                    );

                    dispatch({
                        type: "SET_ITEM",
                        payload: {
                            images: images.filter((img): img is ItemImage => img instanceof ItemImage) // Keep only ItemImage instances
                        }
                    });



                    // Set the primary image if not already set
                    await updatePrimaryImage(state.item, images, uploadResults);
                }
                else {
                    await updatePrimaryImage(state.item, images);
                }

                // Update the image sort orders
                if (images && images.length > 0 && images.some(img => img.sortOrder !== 99)) {
                    await itemService.updateItemImageSortOrders(state.item.itemId, images.filter((img): img is ItemImage => img instanceof ItemImage));

                }


                setTimeout(() => {

                    dispatch({ type: "SET_SUCCESS", payload: "Item saved" });
                    navigate(`/items/view/${state.item.itemId}`, { state: { item: state.item, categories } });

                }, 300);

            } catch (error: any) {
                dispatch({ type: "SET_ERROR", payload: error?.message ?? "An unknown error occurred." });
            }
            finally {
                dispatch({ type: "SET_SUBMITTING", payload: false });
            }

        }
    }


    return (
        <div className="mt-3 mb-5">
            <div className="card rounded-0">
                <div className="card-body  p-4">
                    <h5 className="card-title mb-4">Edit Item</h5>
                    <ItemImageUploadComp
                        item={state.item}
                        itemImages={images}
                        itemImagesToDelete={imagesToDelete}
                        newItemErrors={state.errors}
                        onServiceErrorChange={(error) => dispatch({ type: "SET_ERROR", payload: error })}
                        onItemImagesChange={setImages}
                        onItemChange={(item) => dispatch({ type: "SET_ITEM", payload: item })}
                        onItemImagesToDeleteChange={setImagesToDelete}
                    ></ItemImageUploadComp>
                    {uploadProgress > 0 && (
                        <div className="progress mt-2" style={{ height: '20px' }}>
                            <div
                                className="progress-bar bg-success progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                style={{ width: `${uploadProgress}%` }}
                                aria-valuenow={uploadProgress}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            >
                                {uploadProgress}%
                            </div>
                        </div>
                    )}
                    <form className="row g-3 mt-3" onSubmit={handleEditItemSubmit}>
                        <div className="col-md-12">
                            <label className="form-label">Name</label>
                            <input
                                type="text"
                                className={`form-control ${state.errors.itemName ? 'is-invalid' : ''}`}
                                maxLength={200}
                                placeholder="Name of the item"
                                value={state.item.itemName}
                                onChange={(e) =>
                                    dispatch({ type: "SET_ITEM", payload: { itemName: e.target.value } })
                                }
                            />
                            {state.errors.itemName && <div className="invalid-feedback">{state.errors.itemName}</div>}
                        </div>
                        <div className="col-md-12">
                            <label className="form-label">Description</label>
                            <textarea
                                className={`form-control ${state.errors.description ? 'is-invalid' : ''}`}
                                rows={4}
                                maxLength={2000}
                                placeholder="Description of the item"
                                value={state.item.description}
                                onChange={(e) =>
                                    dispatch({ type: "SET_ITEM", payload: { description: e.target.value } })
                                }
                            />
                            {state.errors.description && <div className="invalid-feedback">{state.errors.description}</div>}

                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Category</label>
                            <select
                                className={`form-control ${state.errors.rootCategory ? 'is-invalid' : ''}`}
                                value={selectedRootCategory ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value ? parseInt(e.target.value) : null;
                                    setSelectedRootCategory(value);

                                    if(value != null) {
                                        const subCats = getSubCategories(value);
                                        if(!subCats || subCats.length == 0) {
                                            setSelectedSubCategory(null);
                                            dispatch({ type: "SET_ITEM", payload: { categoryId: value } });
                                        }

                                    }
                                }}
                            >
                                <option value="">-- Select Category --</option>
                                {categories.map((cat: ItemCategoryModel) => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                            {state.errors.rootCategory && <div className="invalid-feedback">{state.errors.rootCategory}</div>}

                        </div>

                        <div className="col-md-6">
                            <label className="form-label">Subcategory</label>
                            <select
                                className={`form-control ${state.errors.subCategory ? 'is-invalid' : ''}`}
                                value={selectedSubCategory ?? ''}
                                onChange={(e) => {
                                    const value = e.target.value ? parseInt(e.target.value) : null;
                                    setSelectedSubCategory(value);
                                    if (value)
                                        dispatch({ type: "SET_ITEM", payload: { categoryId: value } });
                                }}
                                disabled={subCategories.length === 0}
                            >
                                {(selectedRootCategory && (!subCategories || subCategories.length === 0))
                                    ? <option value="">-- N/A --</option>
                                    : <option value="">-- Select Subcategory --</option>
                                }
                                {subCategories.map((sub) => (
                                    <option key={sub.id} value={sub.id}>
                                        {sub.name}
                                    </option>
                                ))}
                            </select>
                            {state.errors.subCategory && <div className="invalid-feedback">{state.errors.subCategory}</div>}
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Price</label>
                            <input
                                type="number"
                                className={`form-control ${state.errors.price ? 'is-invalid' : ''}`}
                                min={0}
                                step="0.01"
                                value={state.item.price === null || state.item.price === 0 && isNaN(Number(state.item.price)) ? '' : state.item.price}
                                onChange={(e) =>
                                    dispatch({ type: "SET_ITEM", payload: { price: parseFloat(e.target.value) } })
                                }
                            />
                            {state.errors.price && <div className="invalid-feedback">{state.errors.price}</div>}
                        </div>
                        {/* <div className="col-md-12">
                            <label className="form-label">Featured</label>
                            <div className="form-check form-switch ms-4 ps-5 mb-3">
                                <input
                                    className={`form-check-input fs-3 ${state.errors.featured ? 'is-invalid' : ''}`}
                                    type="checkbox"
                                    id="featuredSwitch"
                                    checked={state.item.featured}

                                    onChange={(e) => dispatch({ type: "SET_ITEM", payload: { featured: e.target.checked } })}
                                />

                            </div>
                            {state.errors.featured && (<div className="invalid-feedback" style={{ display: 'block' }}>{state.errors.featured}</div>)}
                        </div>
                        <div className="col-md-12">
                            <label className="form-label">Listed</label>
                            <div className="form-check form-switch  ms-4 ps-5 mb-3">
                                <input
                                    className={`form-check-input fs-3`}
                                    type="checkbox"
                                    id="listedSwitch"
                                    checked={state.item.listed}
                                    onChange={(e) => dispatch({ type: "SET_ITEM", payload: { listed: e.target.checked } })}
                                />

                            </div>
                        </div> */}

                        {state.serviceSuccess && (
                            <div className="col-md-12 clearfix">
                                <div className="alert alert-success p-2 mb-0">{state.serviceSuccess}</div>
                            </div>
                        )}
                        {state.serviceError && (
                            <div className="col-md-12 clearfix">
                                <div className="alert alert-danger p-2 mb-0">{state.serviceError}</div>
                            </div>
                        )}
                        <div className='col-md-12 mt-4 d-flex justify-content-between'>
                            <Link to={`/items/view/${state.item.itemId}`} state={{ item: state.item, categories }} className="btn btn-outline-dark rounded-0">Back</Link>
                            <div className="d-flex">

                                <button type="submit" className="btn btn-dark rounded-0" disabled={state.isSubmitting}>
                                    {state.isSubmitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...</>) : ("Save")}
                                </button>
                            </div>


                        </div>
                    </form>


                </div>
            </div>
        </div>




    );
}

export default EditItemPage;